import EventEmitter from "eventemitter2";
import {
  WebrtcRecorder,
  SocketClient,
  LocalLinkRecorder,
  ScreenShareRecorder,
  WifiInfo,
  AudioRecordModes,
  StartAudioMixRecordConfig,
} from "@core/ability";
import getLocalAudioType from "@/utils/getLocalAudioType";

export { AudioRecordModes } from "@core/ability";
interface RecordHelperStartOptions {
  streamName: string;
  wssPushUrl: string;
  recorder?: "localLink" | "webrtc" | "screenShare";
  wifiInfo?: WifiInfo;
  recordModes?: AudioRecordModes;
}

interface IFormatMixMic extends MediaDeviceInfo {
  isDefault: boolean;
}

export type { WifiInfo, RecordHelperStartOptions };

export class RecorderHelper extends EventEmitter {
  private _audioRecoder:
    | WebrtcRecorder
    | ScreenShareRecorder
    | LocalLinkRecorder;
  private _socketClient: SocketClient;
  private _webrtcRecorder: WebrtcRecorder;
  private _screenShareRecorder: ScreenShareRecorder | null;
  private _localLinkRecorder: LocalLinkRecorder | null;

  constructor() {
    super();
    this._webrtcRecorder = new WebrtcRecorder();
    this._screenShareRecorder = null;
    this._localLinkRecorder = null;
    this._socketClient = new SocketClient();
    this._audioRecoder = this._webrtcRecorder;
  }

  /**
   * 检查是否拥有权限
   */
  public async getPermission() {
    return this._audioRecoder.getpermission();
  }

  /**
   * 开始录制
   * @param param
   * @returns
   */
  public async start({
    streamName,
    wssPushUrl,
    recorder,
    wifiInfo,
    recordModes = AudioRecordModes.MIC,
  }: RecordHelperStartOptions) {
    if (!streamName || !wssPushUrl) {
      return Promise.reject(new Error("streamName or wssPushUrl is null"));
    }
    let recorderType: string | undefined = recorder;
    let recorderData = undefined;
    if (!recorderType) {
      const { type, data } = await getLocalAudioType();
      recorderType = type;
      recorderData = data;
    }
    console.log("local audioLoginType", recorderType, recorderData);
    if (recorderType !== "localLink" && recorderType !== "webrtc") return;

    await this._handleAudioRecoder(
      recordModes,
      recorderType,
      wifiInfo,
      recorderData
    );
    await this._socketClient.start(streamName, wssPushUrl);
    this._handleSocketEvent();
    this._onAudioData();
  }

  /**
   * 暂停录制
   * @returns
   */
  public async pause() {
    this._audioRecoder.pause();
    return this._socketClient.pause();
  }

  /**
   * 继续录制
   * @returns
   */
  public async continue() {
    this._audioRecoder.continue();
    return this._socketClient.continue();
  }

  /**
   * 结束录制
   */
  public async stop() {
    this._audioRecoder.stop();
    await this._socketClient.stop();
    this._socketClient.removeAllListeners();
    this._audioRecoder.removeAllListeners();
  }

  /**
   * 切换麦克风
   * @param deviceId
   */
  public switchMicrophone(mic: MediaDeviceInfo) {
    const formatMic = mic as IFormatMixMic;
    if (this._audioRecoder instanceof WebrtcRecorder) {
      return this._audioRecoder.switchMicrophone(mic.deviceId);
    }
    if (this._audioRecoder instanceof LocalLinkRecorder) {
      const { deviceId: id, label: desc, isDefault } = formatMic;
      return this._audioRecoder.switchMicrophone({
        micInfo: {
          id,
          desc,
          isDefault,
        },
      });
    }
  }

  public getCurrentWifiInfo(){
    if(this._audioRecoder.type === 'screenShare'){
      return this._screenShareRecorder?.getCurrentWifiInfo();
    }
  }

  /**
   * 切换设备
   * @param wifi
   * @returns
   */
  // @Todo deviceType和type应该区分，computer下存在webrtc、localLink
  public async switchDevice(
    deviceType: "computer" | "screenShare",
    deviceData?: WifiInfo | string
  ) {
    let type: string = deviceType;
    let data: StartAudioMixRecordConfig | WifiInfo | string | undefined =
      deviceData;
    // 如果切换回本地，则重新设置麦克风列表的默认麦克风
    if (deviceType === "computer") {
      const { type: localAudioType, data: localAudioData } =
        await getLocalAudioType();
      type = localAudioType;
      if (localAudioData) {
        data = localAudioData;
      }
    }
    console.log("switchDevice", type, data);
    if (this._audioRecoder.type === type) {
      if (type === "webrtc") {
        await (this._audioRecoder as WebrtcRecorder).switchMicrophone(
          data as string
        );
      } else if (type === "localLink") {
        await (this._audioRecoder as LocalLinkRecorder).switchMicrophone(
          data as StartAudioMixRecordConfig
        );
      } else {
        await (this._audioRecoder as ScreenShareRecorder).switchDevice(
          data as WifiInfo
        );
      }
    } else {
      let recorder;
      // 卸载旧的录制器
      this._offAudioData();
      await this._audioRecoder.stop();
      if (type === "webrtc") {
        await this._webrtcRecorder.start(
          data ? { micId: data as string } : undefined
        );
        recorder = this._webrtcRecorder;
      } else if (type === "localLink") {
        if (!this._localLinkRecorder) {
          this._localLinkRecorder = new LocalLinkRecorder();
        }
        await this._localLinkRecorder.start(data as StartAudioMixRecordConfig);
        recorder = this._localLinkRecorder;
      } else {
        if (!this._screenShareRecorder) {
          this._screenShareRecorder = new ScreenShareRecorder();
        }
        await this._screenShareRecorder.start(data as WifiInfo);
        recorder = this._screenShareRecorder;
      }
      // 切换录制器
      this._audioRecoder = recorder;
      this._onAudioData();
    }
  }

  private async _handleAudioRecoder(
    recordModes: AudioRecordModes,
    recorder: "localLink" | "webrtc" | "screenShare",
    wifiInfo?: WifiInfo,
    audioConfig?: StartAudioMixRecordConfig
  ) {
    // 根据recorder选择录制器
    if (recorder === "screenShare") {
      if (!wifiInfo) {
        return Promise.reject(
          new Error("wifiInfo is required when recorder is screenShare")
        );
      }
      // 动态实例传屏录制器
      if (!this._screenShareRecorder) {
        this._screenShareRecorder = new ScreenShareRecorder();
      }
      this._audioRecoder = this._screenShareRecorder;
      await this._audioRecoder.start(wifiInfo);
    } else if (recorder === "localLink") {
      if (!audioConfig) {
        return Promise.reject(
          new Error("AudioConfig is required when recorder is localLink")
        );
      }
      // 动态实例传屏录制器
      if (!this._localLinkRecorder) {
        this._localLinkRecorder = new LocalLinkRecorder();
      }
      this._audioRecoder = this._localLinkRecorder;
      await this._audioRecoder.start(audioConfig);
    } else {
      this._audioRecoder = this._webrtcRecorder;
      await this._audioRecoder.start({ mode: recordModes });
    }
  }

  /**
   * 处理socket事件
   */
  private _handleSocketEvent() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this._socketClient.on("message", (message: any) => {
      this.emit("message", message);
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this._socketClient.on("error", (error: any) => {
      this.emit("error", error);
    });
  }

  /**
   * 监听音频数据
   */
  private _onAudioData() {
    this._audioRecoder.on("data", this._socketClient.reciveData);
    this._audioRecoder.on("error", (error: unknown) => {
      this.emit("error", error);
    });
    // 设备连接失败，连接本地音频
    this._audioRecoder.on("deviceDisconnected", (error) => {
      this.emit("deviceDisconnected", error);
    });
  }

  /**
   * 取消监听音频数据
   */
  private _offAudioData() {
    this._audioRecoder.removeAllListeners();
  }
}
